import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Container from '../components/layout/Container';
import Loading from '../components/common/Loading';
import ErrorMessage from '../components/common/ErrorMessage';
import { getCompetitionDetails } from '../services/public-api';
import { createTeam, listTeams } from '../services/organizer-api';
import { useAuth } from '../hooks/useAuth';
import './TeamManagementPage.css';

/**
 * TeamManagementPage
 * Allows organizers to create and manage teams for a competition
 */
const TeamManagementPage = () => {
  const { competitionId } = useParams();
  const { isOrganizer } = useAuth();
  const [competition, setCompetition] = useState(null);
  const [teams, setTeams] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [formDivisionId, setFormDivisionId] = useState('');

  useEffect(() => {
    if (competitionId) {
      fetchData();
    }
  }, [competitionId]);

  useEffect(() => {
    if (selectedDivision) {
      fetchTeams();
    }
  }, [selectedDivision]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getCompetitionDetails(competitionId);
      setCompetition(data.competition);
      
      if (data.competition.divisions && data.competition.divisions.length > 0) {
        setDivisions(data.competition.divisions);
        const firstDivision = data.competition.divisions[0];
        setSelectedDivision(firstDivision.divisionId || firstDivision);
        setFormDivisionId(firstDivision.divisionId || firstDivision);
      }
    } catch (err) {
      setError(err.message || 'Failed to load competition');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    if (!selectedDivision) return;
    
    try {
      const data = await listTeams(competitionId, selectedDivision);
      setTeams(data.teams || []);
    } catch (err) {
      setError(err.message || 'Failed to load teams');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!teamName.trim() || !formDivisionId) {
      setError('Please provide a team name and select a division');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await createTeam(competitionId, {
        name: teamName.trim(),
        divisionId: formDivisionId,
      });

      setSuccessMessage(`Team "${teamName}" created successfully!`);
      setTeamName('');
      setShowForm(false);
      
      // Refresh teams list if the new team is in the selected division
      if (formDivisionId === selectedDivision) {
        await fetchTeams();
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to create team');
    } finally {
      setSaving(false);
    }
  };

  if (!isOrganizer) {
    return (
      <Container>
        <div className="access-denied">
          <h1>Access Denied</h1>
          <p>You must be an organizer to access this page.</p>
          <Link to="/organizer" className="btn btn-primary">Go to Dashboard</Link>
        </div>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <Loading size="large" />
      </Container>
    );
  }

  if (error && !competition) {
    return (
      <Container>
        <ErrorMessage
          message={error}
          variant="error"
          onRetry={fetchData}
        />
      </Container>
    );
  }

  const filteredTeams = selectedDivision 
    ? teams.filter(t => t.divisionId === selectedDivision)
    : teams;

  return (
    <Container>
      <div className="team-management-page">
        <div className="page-header">
          <Link to={`/organizer/competitions/${competitionId}`} className="back-link">
            ← Back to Competition
          </Link>
          <h1>Manage Teams - {competition?.name}</h1>
        </div>

        {successMessage && (
          <div className="success-banner">
            ✓ {successMessage}
          </div>
        )}

        {error && (
          <ErrorMessage
            message={error}
            variant="error"
            onDismiss={() => setError(null)}
          />
        )}

        {/* Division Filter */}
        {divisions.length > 0 && (
          <div className="division-filter">
            <label>Filter by Division:</label>
            <div className="division-tabs">
              {divisions.map((division) => {
                const divId = division.divisionId || division;
                const divName = division.name || division;
                return (
                  <button
                    key={divId}
                    onClick={() => setSelectedDivision(divId)}
                    className={`division-tab ${selectedDivision === divId ? 'active' : ''}`}
                  >
                    {divName}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Add Team Button */}
        <div className="actions-bar">
          <button
            onClick={() => setShowForm(!showForm)}
            className="btn btn-primary"
          >
            {showForm ? 'Cancel' : '+ Add Team'}
          </button>
        </div>

        {/* Create Team Form */}
        {showForm && (
          <div className="team-form-card">
            <h2>Create New Team</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="teamName">Team Name *</label>
                <input
                  id="teamName"
                  type="text"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="Enter team name"
                  required
                  maxLength={100}
                />
              </div>

              <div className="form-group">
                <label htmlFor="division">Division *</label>
                <select
                  id="division"
                  value={formDivisionId}
                  onChange={(e) => setFormDivisionId(e.target.value)}
                  required
                >
                  <option value="">Select division...</option>
                  {divisions.map((division) => {
                    const divId = division.divisionId || division;
                    const divName = division.name || division;
                    return (
                      <option key={divId} value={divId}>
                        {divName}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setTeamName('');
                    setError(null);
                  }}
                  className="btn btn-secondary"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving || !teamName.trim() || !formDivisionId}
                >
                  {saving ? 'Creating...' : 'Create Team'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Teams List */}
        <div className="teams-section">
          <h2>
            Teams in {divisions.find(d => (d.divisionId || d) === selectedDivision)?.name || selectedDivision}
            <span className="team-count">({filteredTeams.length})</span>
          </h2>

          {filteredTeams.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🏆</div>
              <h3>No Teams Yet</h3>
              <p>Add teams to this division to get started.</p>
            </div>
          ) : (
            <div className="teams-grid">
              {filteredTeams.map((team) => (
                <div key={team.teamId} className="team-card">
                  <div className="team-card-header">
                    <h3>{team.name}</h3>
                    {!team.isActive && (
                      <span className="inactive-badge">Inactive</span>
                    )}
                  </div>
                  <div className="team-card-body">
                    <div className="team-info">
                      <span className="label">Division:</span>
                      <span className="value">
                        {divisions.find(d => (d.divisionId || d) === team.divisionId)?.name || team.divisionId}
                      </span>
                    </div>
                    <div className="team-info">
                      <span className="label">Captains:</span>
                      <span className="value">
                        {team.captainIds && team.captainIds.length > 0 
                          ? team.captainIds.length 
                          : 'None assigned'}
                      </span>
                    </div>
                    <div className="team-info">
                      <span className="label">Team ID:</span>
                      <span className="value team-id">{team.teamId}</span>
                    </div>
                  </div>
                  <div className="team-card-actions">
                    <Link to={`/teams/${team.teamId}`} className="btn-link">
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default TeamManagementPage;
